import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withRouter, browserHistory } from 'react-router';
import cx from 'classnames';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import { connect } from 'react-redux';

import Header from 'components/Header/Header';
import Footer from 'components/Footer/Footer';
import Modal from 'components/Modal/SimpleModal';
import scrollUtil, { getScrollXY } from 'utils/scrollUtil';
import Toast, { toastContext } from 'components/Toast/Toast';
import { Chevron } from 'components/Icons/Icons';
import ExecutionEnvironment from 'fbjs/lib/ExecutionEnvironment';
import { resetSEOMetadataAction } from 'actions/seoMetadataActions';
import getAbValueAction from 'actions/abExperimentsActions';
import PushNotificationPrompt from 'components/PushNotificationWidget/PushNotificationPrompt';

import strings from 'strings.json';
import { booleanToBinaryString, throttleAcrossSessions } from 'utils/miscUtils';
import shallowEqual from 'utils/shallowEqual';
import scrollTracker from 'utils/ScrollTracker';
import omnitureHelper from 'utils/omnitureHelper';

import styles from './Root.css';

import tracker from 'tracker';
import fdpHelper from 'utils/FDPHelper';
import plaHelper from 'utils/plaHelper';
import { trackWebVitals } from 'utils/webVitalsHelper';
import appEventsTracker from 'utils/AppEventsTracker';

const SCROLL_THRESHOLD = 1300;
const TOP_THRESHOLD = 40;
const AB_TOP_THRESHOLD = 0;
let userInteractionFlagsSent = false;

class Root extends Component {
	static contextTypes = {
		store: PropTypes.object.isRequired
	};

	static childContextTypes = {
		toast: toastContext,
		modal: PropTypes.shape({
			show: PropTypes.func.isRequired,
			hide: PropTypes.func.isRequired
		}),
		location: PropTypes.object.isRequired
	};

	state = {
		showFooterSummary: true,
		showGoToTop: false,
		devConsole: null,
		showDevConsole: true
	};

	constructor(props) {
		super(props);
		// Throttle the tracking call to at max once per 30 minutes (1800000 ms).
		this.sendUserInteractionFlagsToOmnitureThrottled = throttleAcrossSessions(
			this.sendUserInteractionFlagsToOmniture,
			1800000,
			'lastBTCallTime'
		);
		this.debouncedTrackAppEventsInFDP = debounce(this.trackAppEventsInFDP, 500);
	}

	getChildContext() {
		// null check for toast and modal is required on safari.
		return {
			toast: {
				show: (...props) => this.toast && this.toast.show(...props),
				showError: (...props) => this.toast && this.toast.showError(...props),
				showSuccess: (...props) => this.toast && this.toast.showSuccess(...props),
				hide: () => this.toast && this.toast.hide()
			},
			modal: {
				show: (...props) => this.modal && this.modal.show(...props),
				hide: (...props) => this.modal && this.modal.hide(...props),
				resetStyles: (...props) => this.modal && this.modal.resetStyles(...props)
			},
			location: this.props.location
		};
	}
	addWindowUnloadHandler = () => {
		if (window && window.addEventListener) {
			window.addEventListener('beforeunload', this.onBeforeUnload);
		}
	};

	sendUserInteractionFlagsToOmniture = (isCursorMoved, isPageScrolled, isKeyPressed) => {
		if (userInteractionFlagsSent) {
			return;
		}

		const userInteractionFlags = booleanToBinaryString([isCursorMoved, isPageScrolled, isKeyPressed]);
		const pageType = get(this.props, 'children.props.route.pathName');

		omnitureHelper.trackLink({
			prop3: pageType,
			prop8: userInteractionFlags
		});

		userInteractionFlagsSent = true;
	};

	onBeforeUnload = () => {
		tracker.endAllViews();
		fdpHelper.flushEvents();
	};

	UNSAFE_componentWillMount() {
		// putting under try catch to throw an exception if IntersectionObserver is not present in window
		if (ExecutionEnvironment.canUseDOM) {
			try {
				tracker.init({
					rootMargin: '-85px 0px 0px 0px',
					batchCallback: events =>
						events.forEach(event => {
							if (event.isPla) {
								plaHelper.trackEvent(event);
							} else {
								fdpHelper.trackEvent(event);
							}
						})
				});
			} catch (e) {
				// ignore error
			}
		}
	}

	listenToUserInteractionEvents() {
		window.addEventListener('keydown', this.catchKeyPress);
		window.addEventListener('mousemove', this.catchCursorMove);
		window.addEventListener('scroll', this.catchScrolling);
	}

	trackAppEventsInFDP = (events, props) => {
		const { pathName } = props.route;
		const path = props.location.pathname;
		if (events && events.length > 0) {
			fdpHelper.trackEvent({
				en: 'AppEvents',
				ev: events,
				mt: {
					appVersion: __RELEASE_VERSION__,
					platform: 'desktop',
					source: 'network',
					pageName: pathName,
					pageUri: path
				}
			});
			appEventsTracker.clear();
		}
	};

	componentDidMount() {
		if (this.props.children.props.route.showTop) this.setGoToTop();
		if (ExecutionEnvironment.canUseDOM) window.scrollTo(0, this.props.children.props.route.scrollTop || 0);

		if (!__SERVER__) {
			this.listenToUserInteractionEvents();

			browserHistory.listen(() => {
				userInteractionFlagsSent = false;
				this.listenToUserInteractionEvents();
			});

			// Tracking AppEvents
			trackWebVitals(metrics => {
				let metricEvents = [];
				for (let name in metrics.raw) {
					if (metrics.raw.hasOwnProperty(name)) {
						metricEvents.push({ name, value: metrics.raw[name] });
					}
				}
				appEventsTracker.addAll(metricEvents);
				this.debouncedTrackAppEventsInFDP(appEventsTracker.events, this.props.children.props);
			});

			if (this.props.enableEEEChanges) {
				// Tracking `External Entry Event`
				const route = get(this.props.children, 'props.route');
				let landingPageName = get(route, 'pathName');
				const trackingData = get(route, 'trackingData');
				if (trackingData) {
					landingPageName = trackingData.pageName || landingPageName;
				}
				fdpHelper.trackEvent({
					en: 'EECE',
					// explicitly using `window.location.href` here, since `props.location` contains only relative url (not host name)
					lu: window.location.href,
					lpn: landingPageName,
					ru: document.referrer,
					aem: get(this.props.location, 'query.appEntryMethod')
				});
			}
		}

		if (__DEBUG_ENV__) {
			import('components/DevConsole/DevConsole').then(module => {
				if (ExecutionEnvironment.canUseDOM) {
					window.addEventListener('keydown', e => {
						if (e.code === 'Backquote' && e.altKey) {
							e.preventDefault();
							this.setState({ showDevConsole: !this.state.showDevConsole });
						}
					});
				}
				this.setState({ devConsole: module.default });
			});
		}
		this.addWindowUnloadHandler();
	}

	catchKeyPress = e => {
		if (ExecutionEnvironment.canUseDOM) {
			window.removeEventListener('keydown', this.catchKeyPress);
			this.sendUserInteractionFlagsToOmnitureThrottled(false, false, true);
		}
	};

	initBandWidthComputation = () => {
		if (navigator && 'serviceWorker' in navigator) {
			navigator.onLine &&
				navigator.serviceWorker.controller &&
				navigator.serviceWorker.controller.postMessage('BANDWIDTH_COMPUTE');
		}
	};

	catchCursorMove = e => {
		if (ExecutionEnvironment.canUseDOM) {
			window.removeEventListener('mousemove', this.catchCursorMove);
			this.sendUserInteractionFlagsToOmnitureThrottled(true, false, false);
		}
	};

	catchScrolling = e => {
		if (ExecutionEnvironment.canUseDOM) {
			window.removeEventListener('scroll', this.catchScrolling);
			this.sendUserInteractionFlagsToOmnitureThrottled(false, true, false);
		}
	};

	UNSAFE_componentWillReceiveProps(nextProps) {
		const { location, resetSEOMetadataAction } = this.props;
		const { location: nextLocation } = nextProps;

		if (
			ExecutionEnvironment.canUseDOM &&
			nextLocation &&
			!shallowEqual(location.query, nextLocation.query) &&
			location.pathname !== nextLocation.pathname
		) {
			scrollTracker.stopTracking();
			resetSEOMetadataAction();
			this.initBandWidthComputation();
		}

		const showTop = nextProps.children.props.route.showTop;
		if (this.props.children.props.route.showTop !== showTop) {
			showTop ? this.setGoToTop() : this.removeGoToTop();
		}
	}

	shouldComponentUpdate(nextProps, nextState) {
		return !shallowEqual(this.props.children, nextProps.children) || !shallowEqual(this.state, nextState);
	}

	componentDidUpdate(nextProps) {
		if (!shallowEqual(this.props.location, nextProps.location))
			window.scrollTo(0, this.props.children.props.route.scrollTop || 0);
	}

	componentWillUnmount() {
		if (ExecutionEnvironment.canUseDOM) {
			window.removeEventListener('scroll', this.scrollListener);
		}
	}

	restoreScroll = (timeout = 0) => {
		setTimeout(() => {
			this.props.router.restoreScroll();
		}, timeout);
	};

	handleGoTop = () => {
		const headerNonSticky = this.props.getAbValue('headerNonSticky', false);
		const { y: scrollToPoint } = getScrollXY();

		scrollUtil(-(scrollToPoint - (headerNonSticky ? AB_TOP_THRESHOLD : TOP_THRESHOLD)), 0.3, 'easeOutSine', window);
	};

	setGoToTop = () => {
		if (ExecutionEnvironment.canUseDOM) {
			window.addEventListener('scroll', this.scrollListener);
		}
	};

	removeGoToTop = () => {
		if (ExecutionEnvironment.canUseDOM) {
			window.removeEventListener('scroll', this.scrollListener);
		}
		this.setState({ showGoToTop: false });
	};

	scrollListener = e => {
		const scrollPos = typeof window.scrollY !== 'undefined' ? window.scrollY : window.pageYOffset;
		if (scrollPos > SCROLL_THRESHOLD && !this.state.showGoToTop) {
			this.setState({ showGoToTop: true });
		}
		scrollPos <= SCROLL_THRESHOLD && this.state.showGoToTop && this.setState({ showGoToTop: false });
	};

	toastRef = c => (this.toast = c);
	rootRef = c => (this.root = c);
	modalRef = c => (this.modal = c);

	render() {
		const { minimalHeader, minimalFooter, noFooter = false, onlyChildren, isLoginPage, isHelpCentre, isHomePage } =
			get(this.props, ['children', 'props', 'route']) || {};
		const headerNonSticky = this.props.getAbValue('headerNonSticky', false);
		const DevConsole = this.state.devConsole;
		const actualPageType = get(this.props, 'children.props.route.pathName');
		const omniturePageType = get(this.props, 'children.props.route.trackingData.pageType') || actualPageType;
		if (onlyChildren) return this.props.children;

		// NOTE: If there's an unhandled promise rejection anywhere down the
		// hierarchy, the code that depends on context props will not work
		// properly. In this case, the major ones are Toast and Modal. So these
		// are kept at the top of the children.
		return (
			<div>
				<Toast ref={this.toastRef} />
				<Modal ref={this.modalRef} />

				{DevConsole && this.state.showDevConsole && <DevConsole />}

				<Header
					minimal={minimalHeader}
					pageType={actualPageType}
					isLoginPage={isLoginPage}
					isHelpCentre={isHelpCentre}
					isHomePage={isHomePage}
					omniturePageType={omniturePageType}
				/>
				{React.cloneElement(this.props.children, { restoreScroll: this.restoreScroll })}
				{!noFooter && <Footer showSummary={this.state.showFooterSummary} minimal={minimalFooter} />}
				<div
					className={cx(
						styles['back-to-top'],
						{ [styles['back-to-top-ab']]: headerNonSticky },
						styles['zion-header-ab']
					)}
				>
					<div
						className={cx(styles['element'], { [styles['show']]: this.state.showGoToTop })}
						onClick={this.handleGoTop}
					>
						<Chevron className={styles['chevron']} pathClassName={styles['chevron-path']} />
						<span>{strings['backToTop']}</span>
					</div>
				</div>
				<PushNotificationPrompt />
			</div>
		);
	}
}

const mapStateToProps = state => {
	return {
		enableABV2Changes: get(state.config, 'data.globalConfig.enableABV2Changes', false),
		enableEEEChanges: get(state.config, 'data.globalConfig.enableEEEChanges', false)
	};
};

export default connect(mapStateToProps, { resetSEOMetadataAction, getAbValue: getAbValueAction })(withRouter(Root));
